/**
 * Shared types for `@confluence/content-types-bridge` and its consumers.
 *
 * Lives in a leaf package to avoid circular dependencies. Add types here when
 * they need to be referenced by both the bridge and one or more other
 * Confluence packages.
 */

/**
 * All registered product keys for the cross-product command bus.
 */
export const CONTENT_TYPE_PRODUCT_KEYS = [
	'whiteboards',
	'databases',
	'slides',
	'confluence',
] as const;

export type ContentTypeProductKey = (typeof CONTENT_TYPE_PRODUCT_KEYS)[number];

export type ContentTypeCommandType = 'copy' | 'analytics' | 'export';

/**
 * Per-command payload types.
 * Signal-only commands ('copy', 'analytics') carry no payload field — matches
 * the V1 triggerEvent() pattern where these carry no arguments.
 * Add a new union member when a new command type is introduced.
 */

/**
 * Payload for the 'export' command. Sent by AFM, consumed by the canvas app.
 *
 * Discriminated by `product` so each Confluence content type can declare its
 * own set of supported export formats without leaking formats across products
 * (e.g. slides can export to PowerPoint, PDF and Google Slides).
 *
 * Add a new union member when a new product surfaces V2 export, and add a new
 * `format` literal when an existing product gains a new export option.
 */
export type ExportCommandPayload = {
	product: 'slides';
	format: 'pptx' | 'pdf';
};

/**
 * A discriminated union of all cross-product commands.
 * The `type` field narrows the payload type at compile time.
 * Signal-only commands ('copy', 'analytics') carry no payload field.
 */
export type ContentTypeCommand =
	| { type: 'export'; payload: ExportCommandPayload }
	| { type: 'copy' }
	| { type: 'analytics' };
